import {EMBEDLY_KEY, EMBEDLY_HOST} from './constants';
import {
  isYouTubeUrl,
  getYouTubeParams,
  autoplay,
  cleanUrl,
  type YouTubeParams,
  removeYouTubeParams,
} from '@packages/systems/core/utils/MediaUtils';
import {getVideoId} from '@packages/systems/core/utils/FigureUtils';
import fetch from 'isomorphic-fetch';

const FORCE_HTTPS_EMBEDS = false;

export type VideoHelperResponse = {
  url: string;
  metadata?: {
    aspectRatio: number;
    description: string;
    height: number;
    html: string;
    source: string;
    thumb: {
      h: number;
      url: string;
      w: number;
    };
    title: string;
    url: string;
    width: number;
    videoId?: string;
    youTubeParams?: YouTubeParams;
  };
};

type Data =
  | {
      type: 'video' | 'rich';
      html: string;
      provider_url: string;
      thumbnail_url: string;
      thumbnail_width: number;
      thumbnail_height: number;
      width: number;
      height: number;
      description: string;
      title: string;
    }
  | {
      type: 'error';
      error_message: string;
    };

export async function request(url: string): Promise<VideoHelperResponse> {
  // Add protocol to make embed.ly happy
  if (!/^http/.test(url.toLowerCase())) {
    url = 'http://' + url;
  }

  // cleanup url because youtu.be/ID is no longer valid for embedly
  url = cleanUrl(url);
  const isYouTube = isYouTubeUrl(url);

  const queryParams = new URLSearchParams({
    url: isYouTube ? removeYouTubeParams(url) : url,
    key: EMBEDLY_KEY,
    maxWidth: '940',
    width: '940',
    secure: FORCE_HTTPS_EMBEDS.toString(),
    autoplay: autoplay(url).toString(),
    native: 'true',
  }).toString();

  const response = await fetch(`${EMBEDLY_HOST}${queryParams}`);
  const data = (await response.json()) as Data;

  if (data.type === 'error') {
    return Promise.reject(`Error: ${data.error_message}`);
  }

  if (data.type !== 'video' && data.type !== 'rich') {
    return Promise.reject('Invalid embed type: ' + data.type);
  }

  const result = {
    url,
    metadata: {
      ...data,
      /**
       * At some point, code came dependent on the below object shape,
       * but other code depends on the exact shape that the API returns.
       * To make all callsites happy, we spread the original data, but also modify
       * what needs to be modified so we don't break it.
       * Ideally, we'd only need to spread `...data` and get rid of our transform below.
       */
      url,
      html: data.html,
      source: data.provider_url,
      thumb: {
        url: data.thumbnail_url,
        w: data.thumbnail_width,
        h: data.thumbnail_height,
      },
      width: data.width,
      height: data.height,
      aspectRatio: data.height / data.width,
      desc: data.description,
      title: data.title,
    },
  } as const;

  if (isYouTube) {
    const youTubeParams: YouTubeParams = getYouTubeParams(url) || ({} as any);

    // @ts-expect-error - TS2540 - Cannot assign to 'metadata' because it is a read-only property.
    result.metadata = {
      ...result.metadata,
      videoId: getVideoId(url).id,
      youTubeParams,
    };
  }

  return result;
}

export type OembedMetadataResult = {
  url: string;
  resolved: boolean;
  title?: string;
  description?: string;
  thumbnailUrl?: string | null;
  providerName?: string | null;
  faviconUrl?: string | null;
};

const OEMBED_BATCH_SIZE = 20;

type OembedSuccessData = {
  type: 'link' | 'photo' | 'video' | 'rich';
  url?: string;
  title?: string;
  description?: string;
  thumbnail_url?: string;
  provider_name?: string;
  favicon_url?: string;
};

type OembedErrorData = {
  type: 'error';
  error_message?: string;
  url?: string;
};

type OembedData = OembedSuccessData | OembedErrorData;

/**
 * Reported once per chunk when embed.ly cannot be reached or returns a non-2xx
 * status, so a total outage is observable (degraded vs. healthy) instead of
 * collapsing indistinguishably into per-URL `resolved: false` misses.
 * `reason`/`status` are intentionally low-cardinality so consumers can safely
 * tag a bounded counter or log.
 */
export type OembedBatchFailure = {
  reason: 'http_error' | 'network_error';
  status?: number;
  urlCount: number;
};

export type OembedBatchFailureHandler = (failure: OembedBatchFailure) => void;

function normalizeCitationUrl(url: string): string {
  if (!/^http/i.test(url)) {
    return 'http://' + url;
  }

  return url;
}

function chunkUrls(urls: string[], size: number): string[][] {
  const chunks: string[][] = [];

  for (let index = 0; index < urls.length; index += size) {
    chunks.push(urls.slice(index, index + size));
  }

  return chunks;
}

function mapOembedItem(
  originalUrl: string,
  item: OembedData | undefined
): OembedMetadataResult {
  if (!item || item.type === 'error') {
    return {
      url: originalUrl,
      resolved: false,
    };
  }

  return {
    url: originalUrl,
    resolved: true,
    title: item.title ?? '',
    description: item.description ?? '',
    thumbnailUrl: item.thumbnail_url ?? null,
    providerName: item.provider_name ?? null,
    faviconUrl: item.favicon_url ?? null,
  };
}

/**
 * Build the oEmbed query string. Embed.ly's batch `urls` param is a
 * comma-separated list — each URL must be encoded, but the commas must stay
 * literal. `URLSearchParams` encodes `,` as `%2C`, which makes embed.ly treat
 * the whole list as a single URL.
 */
function buildOembedQueryParams(normalizedUrls: string[]): string {
  const encodedUrls = normalizedUrls.map(encodeURIComponent).join(',');

  return [
    `urls=${encodedUrls}`,
    `key=${encodeURIComponent(EMBEDLY_KEY)}`,
    'maxwidth=940',
    `secure=${FORCE_HTTPS_EMBEDS}`,
    'native=true',
  ].join('&');
}

async function fetchOembedBatch(
  originalUrls: string[],
  onBatchFailure?: OembedBatchFailureHandler
): Promise<OembedMetadataResult[]> {
  const normalizedUrls = originalUrls.map(normalizeCitationUrl);
  const queryParams = buildOembedQueryParams(normalizedUrls);

  const unresolvedBatch = (): OembedMetadataResult[] =>
    originalUrls.map((originalUrl) => ({
      url: originalUrl,
      resolved: false,
    }));

  try {
    const requestUrl = `${EMBEDLY_HOST}${queryParams}`;
    const response = await fetch(requestUrl);

    // A non-2xx (429 rate-limit, 5xx outage) is a batch-level failure, not a
    // per-URL miss. Signal it distinctly and skip parsing the body.
    if (!response.ok) {
      onBatchFailure?.({
        reason: 'http_error',
        status: response.status,
        urlCount: originalUrls.length,
      });

      return unresolvedBatch();
    }

    const data = (await response.json()) as OembedData | OembedData[];
    const items = Array.isArray(data) ? data : [data];

    return originalUrls.map((originalUrl, index) =>
      mapOembedItem(originalUrl, items[index])
    );
  } catch {
    onBatchFailure?.({
      reason: 'network_error',
      urlCount: originalUrls.length,
    });

    return unresolvedBatch();
  }
}

/**
 * Bulk-resolve page metadata for citation URLs via embed.ly oEmbed.
 * Preserves input order and chunks requests at 20 URLs per call.
 *
 * `onBatchFailure` is invoked once per chunk that fails to reach embed.ly or
 * gets a non-2xx response, letting server callers emit a bounded operator
 * signal (counter/log) for degraded vs. healthy without this shared module
 * depending on server-only telemetry.
 */
export async function resolveOembedMetadata(
  urls: string[],
  onBatchFailure?: OembedBatchFailureHandler
): Promise<OembedMetadataResult[]> {
  if (urls.length === 0) {
    return [];
  }

  const chunks = chunkUrls(urls, OEMBED_BATCH_SIZE);
  const chunkResults = await Promise.all(
    chunks.map((chunk) => fetchOembedBatch(chunk, onBatchFailure))
  );

  return chunkResults.flat();
}
